import React from 'react';
import CountBadge, {
  COUNT_BADGE_SIZE_XS,
  COUNT_BADGE_TONE_ACCENT,
  COUNT_BADGE_TONE_NEUTRAL,
} from '../CountBadge';
import { Root, Segment } from './SegmentedControl.styled';

export interface SegmentedControlOption<T extends string = string> {
  id: T;
  label: string;
  /** Optional count badge shown after the label. */
  count?: number;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
  dataTestId?: string;
}

function SegmentedControlInner<T extends string = string>({
  options,
  value,
  onChange,
  ariaLabel = 'View',
  dataTestId = 'segmented-control',
}: SegmentedControlProps<T>) {
  return (
    <Root role="group" aria-label={ariaLabel} data-testid={dataTestId}>
      {options.map((opt) => {
        const isActive = opt.id === value;
        return (
          <Segment
            key={opt.id}
            type="button"
            aria-pressed={isActive}
            disabled={opt.disabled}
            $isActive={isActive}
            onClick={() => onChange(opt.id)}
            data-testid={`${dataTestId}__segment-${opt.id}`}
          >
            <span>{opt.label}</span>
            {typeof opt.count === 'number' && (
              <CountBadge
                tone={
                  isActive ? COUNT_BADGE_TONE_ACCENT : COUNT_BADGE_TONE_NEUTRAL
                }
                size={COUNT_BADGE_SIZE_XS}
                dataTestId={`${dataTestId}__count-${opt.id}`}
              >
                {opt.count}
              </CountBadge>
            )}
          </Segment>
        );
      })}
    </Root>
  );
}

const SegmentedControl = SegmentedControlInner as <T extends string = string>(
  props: SegmentedControlProps<T>,
) => React.ReactElement;

export default SegmentedControl;
